import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  List,
  ListItem,
  ListItemSecondaryAction,
  ListItemText,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import ArrowBackRoundedIcon from "@mui/icons-material/ArrowBackRounded";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import DeleteOutlineRoundedIcon from "@mui/icons-material/DeleteOutlineRounded";
import EditRoundedIcon from "@mui/icons-material/EditRounded";
import { ClientLayout } from "../components/ClientLayout";
import { clientApi, type ClientAddress } from "../services/clientApi.service";
import { formatLocationLine } from "@/utils/location";
import { buildAddressFormPath } from "../utils/addressFormReturn";

/**
 * Liste des adresses du client connecté.
 */
export function ClientAddressesPage(): JSX.Element {
  const navigate = useNavigate();
  const [addresses, setAddresses] = useState<ClientAddress[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    async function load(): Promise<void> {
      setLoading(true);
      setError(null);
      try {
        const addrs = await clientApi.listAddresses();
        if (cancelled) return;
        setAddresses(addrs);
      } catch (err) {
        if (cancelled) return;
        const message =
          (err as { response?: { data?: { message?: string } } }).response?.data?.message ??
          "Impossible de charger vos adresses.";
        setError(message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    void load();
    return () => {
      cancelled = true;
    };
  }, []);

  const sortedAddresses = useMemo(
    () =>
      [...addresses].sort((a, b) => {
        if (a.isDefault !== b.isDefault) return a.isDefault ? -1 : 1;
        return a.displayOrder - b.displayOrder;
      }),
    [addresses],
  );

  async function handleDelete(addr: ClientAddress): Promise<void> {
    if (!window.confirm("Supprimer cette adresse ?")) return;
    try {
      await clientApi.deleteAddress(addr.id);
      setAddresses((prev) => prev.filter((a) => a.id !== addr.id));
    } catch (err) {
      const message =
        (err as { response?: { data?: { message?: string } } }).response?.data?.message ??
        "Suppression impossible.";
      setError(message);
    }
  }

  return (
    <ClientLayout title="Mes adresses">
      <Stack spacing={2}>
        <Stack direction="row" alignItems="center" spacing={1}>
          <IconButton onClick={() => navigate(-1)} aria-label="Retour">
            <ArrowBackRoundedIcon />
          </IconButton>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Mes adresses
          </Typography>
        </Stack>

        {error ? <Alert severity="error">{error}</Alert> : null}

        <Paper sx={{ p: 0, overflow: "hidden" }}>
          {loading ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
              <CircularProgress size={24} />
            </Box>
          ) : sortedAddresses.length === 0 ? (
            <Box sx={{ p: 3, textAlign: "center" }}>
              <Typography color="text.secondary">Aucune adresse enregistrée.</Typography>
              <Typography variant="caption" color="text.secondary">
                Ajoutez votre adresse de livraison pour passer commande.
              </Typography>
            </Box>
          ) : (
            <List disablePadding>
              {sortedAddresses.map((addr, idx) => (
                <ListItem
                  key={addr.id}
                  divider={idx < sortedAddresses.length - 1}
                  sx={{ py: 1.5 }}
                >
                  <ListItemText
                    primaryTypographyProps={{ component: "div" }}
                    secondaryTypographyProps={{ component: "div" }}
                    primary={
                      <Stack direction="row" alignItems="center" spacing={1}>
                        <Typography component="span" sx={{ fontWeight: 500 }}>
                          {addr.label || addr.address}
                        </Typography>
                        {addr.isDefault ? (
                          <Chip size="small" label="Par défaut" color="secondary" />
                        ) : null}
                      </Stack>
                    }
                    secondary={
                      <>
                        {addr.label ? (
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            component="span"
                            sx={{ display: "block" }}
                          >
                            {addr.address}
                          </Typography>
                        ) : null}
                        {addr.postalCode || addr.city ? (
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            component="span"
                            sx={{ display: "block" }}
                          >
                            {formatLocationLine(addr.postalCode, addr.city)}
                          </Typography>
                        ) : null}
                      </>
                    }
                  />
                  <ListItemSecondaryAction>
                    <IconButton
                      onClick={() =>
                        navigate(buildAddressFormPath("edit", "addresses", addr.id))
                      }
                      aria-label="Modifier"
                      size="small"
                    >
                      <EditRoundedIcon />
                    </IconButton>
                    <IconButton
                      onClick={() => handleDelete(addr)}
                      aria-label="Supprimer"
                      size="small"
                    >
                      <DeleteOutlineRoundedIcon />
                    </IconButton>
                  </ListItemSecondaryAction>
                </ListItem>
              ))}
            </List>
          )}
        </Paper>

        <Button
          variant="contained"
          color="secondary"
          startIcon={<AddRoundedIcon />}
          onClick={() => navigate(buildAddressFormPath("new", "addresses"))}
        >
          Ajouter une adresse
        </Button>
      </Stack>
    </ClientLayout>
  );
}
